import { Stack } from '@mantine/core';
import { useNavigate } from 'react-router';
import { useCreateFinancialItem } from 'src/financial/financial-data/financial-item/useCreateFinancialItem';
import { FinancialItems } from 'src/financial/financial-data/hooks/useFinancialItems';
import { SectionAddAction } from 'src/cashflows/components/SectionAddButton';
import { getCategoryEntries } from './addProductEntries';
import { FinancialSectionTable } from './FinancialSectionTable';
import { FinancialRow, buildCategories, extractRows } from './financialRows';

type Props = {
  clientId: string;
  clientFirstName: string;
  financialItems: FinancialItems;
};

export function FinancialContentRework({ clientId, clientFirstName, financialItems }: Props) {
  const navigate = useNavigate();
  const { beginCreate, modals } = useCreateFinancialItem({ clientId });

  const categories = buildCategories(extractRows(financialItems, clientFirstName));

  const handleRowClick = (row: FinancialRow) => {
    navigate(`/client/${clientId}/financial-items/${row.navProduct}/${row.id}`);
  };

  const addActionForCategory = (key: string): SectionAddAction | undefined => {
    switch (key) {
      case 'spendingAccounts':
        return { onAdd: () => beginCreate('SPENDING_ACCOUNT', 'SpendingAccount') };
      case 'protections':
        return { onAdd: () => beginCreate('PROTECTION', 'Protection') };
      case 'assets':
        return { entries: getCategoryEntries('ASSET'), onSelect: p => beginCreate('ASSET', p) };
      case 'liabilities':
        return {
          entries: getCategoryEntries('LIABILITY'),
          onSelect: p => beginCreate('LIABILITY', p),
        };
      default:
        return undefined;
    }
  };

  return (
    <Stack gap="lg">
      {categories.map(category => (
        <FinancialSectionTable
          key={category.key}
          title={category.title}
          rows={category.rows}
          total={category.total}
          totalLabel={category.totalLabel}
          subSections={category.subSections}
          onRowClick={handleRowClick}
          addAction={addActionForCategory(category.key)}
          hideTotal={category.key === 'protections'}
        />
      ))}
      {modals}
    </Stack>
  );
}
